import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

interface User {
  id: string;
  email?: string;
  phone?: string;
  firstName: string;
  lastName?: string;
  userType: string;
  tenantId?: string;
  zoneId?: string;
  zoneIds?: string[];
  roleSlugs?: string[];
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  permissions: string[];
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem('user') || 'null'),
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  permissions: JSON.parse(localStorage.getItem('permissions') || '[]'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User;
        accessToken: string;
        refreshToken: string;
        permissions: string[];
      }>
    ) => {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.permissions = action.payload.permissions;
      state.isAuthenticated = true;

      localStorage.setItem('user', JSON.stringify(action.payload.user));
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
      localStorage.setItem('permissions', JSON.stringify(action.payload.permissions));
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.permissions = [];
      state.isAuthenticated = false;
      localStorage.clear();
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (!state.user) return;
      state.user = { ...state.user, ...action.payload };
      localStorage.setItem('user', JSON.stringify(state.user));
    },
  },
});

export const { setCredentials, logout, updateUser } = authSlice.actions;
export default authSlice.reducer;

export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectPermissions = (state: { auth: AuthState }) => state.auth.permissions;
export const selectIsSuperAdmin = (state: { auth: AuthState }) =>
  state.auth.user?.userType === 'super_admin';

export const selectIsAdminStaff = (state: { auth: AuthState }) => {
  const t = state.auth.user?.userType;
  return t === 'super_admin' || t === 'client_admin' || t === 'employee';
};

export const selectRoleSlugs = (state: { auth: AuthState }) => state.auth.user?.roleSlugs || [];

export const selectIsCityAdmin = (state: { auth: AuthState }) => {
  const user = state.auth.user;
  return user?.userType === 'employee' && (user.roleSlugs || []).includes('city_admin');
};

export const selectStaffZoneIds = createSelector(
  [(state: { auth: AuthState }) => state.auth.user],
  (user) => {
    if (!user || user.userType === 'super_admin' || user.userType === 'client_admin') return [];
    if (user.zoneIds?.length) return user.zoneIds;
    return user.zoneId ? [user.zoneId] : [];
  },
);
